import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'
import { UserContext } from '../../../App'
import moment from 'moment'
import { useMatch, useNavigate } from 'react-router-dom'
import { useTreatmentLocationSnapshot } from './snapshot'
import { Box } from '@mui/material'
import ZoneDrawer from './ZoneDrawer'
import TreatmentCentreWorkspace from './TreatmentCentreWorkspace'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'

export const TreatmentLocationContext = createContext({})

export const TreatmentCentre = ({
  selectedLocation,
  onExit,
  workstationMode = false,
}) => {
  const { siteMode, setSiteMode } = useContext(SiteModeContext)
  const { user } = useContext(UserContext)
  const { setPageTitle } = useDocumentTitle()
  const [currentTime, setCurrentTime] = useState(moment())

  const [openAddNewPatient, setOpenAddNewPatient] = useState(false)
  const [showTreatmentLocationInfo, setShowTreatmentLocationInfo] =
    useState(false)

  const { data, loading, linkedControlID, refresh, syncState, lastUpdated } =
    useTreatmentLocationSnapshot(selectedLocation, 1000)

  const treatmentZones = data?.treatmentZones
  const treatmentAssignments = data?.treatmentAssignments
  const pendingAssignments = data?.pendingAssignments
  const linkedControlRoom = data?.controlRoom
  const title = data?.title ?? 'Treatment Location'
  const status = data?.status ?? 'Unknown'

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(moment()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (title) {
      setPageTitle(title)
    }
  }, [title, setPageTitle])

  const stale = syncState === 'offline' || syncState === 'delayed'
  const contextValue = useMemo(
    () => ({
      locationID: selectedLocation,
      centre: data,
      loading,
      linkedControlID,
      linkedControlRoom,
      title,
      status,
      treatmentZones,
      treatmentAssignments,
      pendingAssignments,
      syncState,
      lastUpdated,
      refresh,
      onExit,
    }),
    [
      data,
      lastUpdated,
      linkedControlID,
      linkedControlRoom,
      loading,
      onExit,
      pendingAssignments,
      refresh,
      selectedLocation,
      status,
      syncState,
      title,
      treatmentAssignments,
      treatmentZones,
    ],
  )

  if (!selectedLocation) return null

  return (
    <TreatmentLocationContext.Provider value={contextValue}>
      <Box minHeight="100vh">
        <TreatmentCentreWorkspace
          title={title}
          locationID={selectedLocation}
          centre={data}
          status={status}
          snapshotZones={treatmentZones}
          treatmentAssignments={treatmentAssignments}
          pendingAssignments={pendingAssignments}
          loading={loading}
          canConfigure={!!data?.canConfigure}
          syncState={syncState}
          lastUpdated={lastUpdated}
          refresh={refresh}
          workstationMode={workstationMode}
          onExit={onExit}
        />
      </Box>
    </TreatmentLocationContext.Provider>
  )
}

export const TreatmentWorkstationRoute = () => (
  <TreatmentLocationRoute workstationMode />
)

const TreatmentLocationRoute = ({ workstationMode = false }) => {
  const match = useMatch(
    workstationMode
      ? '/treatmentworkstations/:locationID'
      : '/treatmentlocations/:locationID',
  )
  const locationID =
    match?.params?.locationID ?? match?.params?.treatmentLocationID

  const navigate = useNavigate()
  const {
    setSiteMode,
    setSelectedTreatmentLocation,
    setTreatmentLocationWorkstationMode,
  } = useContext(SiteModeContext)

  useEffect(() => {
    if (locationID) {
      setSelectedTreatmentLocation(locationID)
      setTreatmentLocationWorkstationMode(workstationMode)
      setSiteMode('treatmentLocation')
    }
  }, [
    locationID,
    setSelectedTreatmentLocation,
    setSiteMode,
    setTreatmentLocationWorkstationMode,
    workstationMode,
  ])

  const handleExit = () => {
    setSiteMode('normal')
    setSelectedTreatmentLocation(null)
    setTreatmentLocationWorkstationMode(false)
    navigate('/commandcontrol/treatmentlocations')
  }

  if (!locationID) return null

  return (
    <TreatmentCentre
      selectedLocation={locationID}
      onExit={handleExit}
      workstationMode={workstationMode}
    />
  )
}
export default TreatmentLocationRoute
